import { message } from 'ant-design-vue'
import type { NuxtApp } from '#app'
import dayjs from 'dayjs'
import type { IResponse, RequestConfig } from './interface/common'

import { logoutForBe } from './module/auth'
import { COOKIE_KEY } from '~/constants/config'
import { consola } from '@/utils/logger'

export const throwErrorCode = 100001

export enum ServerErrorCode {
  USER_EXIST_ERROR = 11004,
  EMAIL_NOT_REGISTERED = 11016,
}

export async function fetch<K extends IResponse = IResponse>(
  url: string,
  config: RequestConfig,
  nuxtApp?: NuxtApp
): Promise<IResponse<K['data'] | undefined>> {
  let params: any

  // 暂时这么处理，对于patch以及其他请求可能有漏洞。最好方案是nginx设置响应头
  if (config.method?.toLowerCase() === 'get' || config.params) {
    params = { _t: new Date().getTime(), ...config.params }
  }

  const mergedConfig: RequestConfig = {
    autoErrorHandle: true,
    credential: true,
    ...config,
    ...{ params },
  }

  let session: any = {}
  let app = nuxtApp
  const runtimeConfig = nuxtApp?.$config || useRuntimeConfig()
  // 当接口不需要走任何鉴权，且执行在服务端运行时
  if (mergedConfig.credential) {
    app = nuxtApp || tryUseNuxtApp() || undefined
    session = import.meta.client
      ? localGetItem(COOKIE_KEY.USER_TOKEN)
      : getCookie(COOKIE_KEY.USER_TOKEN, app)
  }

  const startTime = Date.now()
  return await $fetch(url, {
    baseURL: runtimeConfig.public.apiBase,
    timeout: 30 * 1000,

    onRequest({ options, error, response }) {
      // 防一手，后端可能某些环境的接口鉴权一致
      if (config.credential === false) {
        return
      }

      options.headers = new Headers({
        ...mergedConfig.headers,
        'Accept-Language': isChinese() ? 'zh-CN' : 'en',
      })

      if (!runtimeConfig.public.privatization) {
        options.headers.set(
          'Session-User',
          getCookie(COOKIE_KEY.USER_RANDOM_ID, app)
        ) // 后端埋点数据需要随机id
      }

      if (!session?.user_id) {
        return
      }

      if (!options.headers.get('Authorization')) {
        options.headers.set(
          'Authorization',
          `token ${session.user_id} ${session.key}`
        )
      }
    },
    onRequestError({ request, options, error }) {
      consola.warn('[Fetch Error]', request, error)
    },
    async onResponseError({ options, error, response }) {
      if (response?.status === 401) {
        if (import.meta.client) {
          message.destroy()
          const permissionData = usePermissionData()
          await useLoginOut(false)

          const link = permissionData.value?.config?.logout_url
            ? permissionData.value.config.logout_url
            : location.origin +
              `/login?redirect=${encodeURIComponent(location.href)}`
          location.href = link!
          return
        } else if (app?.payload.state) {
          deleteCookie(COOKIE_KEY.USER_TOKEN, app)
          // const permissionData = toRef(app.payload.state, '$spermissionData')
          const userInfo = toRef(app.payload.state, '$suserInfo')
          const accountList = toRef(app.payload.state, '$saccountList')
          const session = toRef(app.payload.state, `$s${COOKIE_KEY.USER_TOKEN}`)
          await concurrency([
            logoutForBe(),
            fetch('/api/session', { method: 'DELETE', baseURL: '' }),
          ])
          userInfo.value = undefined
          accountList.value = []
          session.value = undefined
          // 登录态失效，则禁用缓存，服务端不处理重定向（因为没有登录态拿不到重定向地址）
          if (app.ssrContext) {
            app.ssrContext.event.noStore = true
          }
        }
      }
      import.meta.client &&
        message.error(error?.message || response._data.msg || '系统异常')
    },
    onResponse({ response, options }) {
      const timeEnd = Date.now()
      const deltaTime = +(timeEnd - startTime).toFixed(6)
      const time = dayjs(startTime).format('YYYY-MM-DD HH:mm:ss:SSS')

      consola[response.status === 200 ? 'info' : 'error'](
        `[${time}] ${config.method || 'GET'} ${
          response.status
        } ${url} ${deltaTime}ms`
      )

      if (
        response.status === 200 &&
        mergedConfig.autoErrorHandle &&
        response._data.code !== 0
      ) {
        // 这里是业务定义的错误码
        import.meta.client && message.error(response._data.msg)

        return Promise.reject(response._data.msg)
      }
    },
    parseResponse(responseText) {
      try {
        const data = JSON.parse(responseText)

        const assemblyData =
          'error_code' in data
            ? { ...data, code: data.error_code, msg: data.error_reason }
            : { data, code: 0, msg: '' } // 消息中心没有data层，因此这里兼容

        return assemblyData
      } catch (error) {
        console.error(error)
        return {
          data: undefined,
          code: throwErrorCode,
          msg: '系统异常',
        }
      }
    },
    ...mergedConfig,
  })
}
